import { autoinject } from "aurelia-dependency-injection";
import * as LocalForage from "localforage";
import * as Enumerable from "linq";

@autoinject
export class CacheClientService {
    public sessionStorageHolder: SessionStorage = new SessionStorage();

    public put<T>(key: string, value: T, location: CacheLocation = CacheLocation.LocalStorage): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (!key) {
                reject();
                return;
            }
            switch (location) {
                case CacheLocation.LocalStorage:
                    LocalForage.setItem(key, value, function (err) {
                        // if err is non-null, we got an error
                        if (err) {
                            reject(err);
                        } else {
                            resolve();
                        }
                    });
                    break;
                case CacheLocation.SessionStorage:
                    {
                        const added = this.sessionStorageHolder.addItem(key, value);
                        if (added)
                            resolve();
                        break;
                    }
                default:
            }
        });
    }

    private startsWith(str: string, searchString: string) {
        return str.substr(0, searchString.length) === searchString;
    }

    public getKeysByCacheCategory<T>(category: string, location: CacheLocation = CacheLocation.LocalStorage): Promise<T[]> {
        return new Promise<T[]>((resolve, reject) => {
            switch (location) {
                case CacheLocation.LocalStorage:
                    LocalForage.keys().then(x => {
                        const promises = Enumerable.from<string>(x).where(x => this.startsWith(x, category)).select(x => {
                            return this.get<T>(x);
                        }).toArray();

                        Promise.all(promises).then(resolve, reject)
                    });
                    break;
                case CacheLocation.SessionStorage:
                    {
                        const keys = this.sessionStorageHolder.getKeys();
                        const values = Enumerable.from<string>(keys).where(x => this.startsWith(x, category)).select(key => {
                            return this.sessionStorageHolder.getItem(key);
                        }).toArray();
                        resolve(values);
                        break;
                    }
                default:
            }
        });
    }

    public get<T>(key: string, location: CacheLocation = CacheLocation.LocalStorage, rejectOnNotFound: boolean = true): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            if (!key) {
                reject();
            }
            switch (location) {
                case CacheLocation.LocalStorage:
                    LocalForage.getItem(key, function (err, value) {
                        // if err is non-null, we got an error. otherwise, value is the value
                        const valueObj: T = <T>value;
                        if (err || (rejectOnNotFound && !valueObj)) {
                            reject(err);
                        } else {
                            resolve(valueObj);
                        }
                    });
                    break;
                case CacheLocation.SessionStorage:
                    {
                        const valueObj: T = <T>this.sessionStorageHolder.getItem(key);
                        resolve(valueObj);
                        break;
                    }
                default:
            }
        });
    }

    public updateItem(key: string, value: any, location: CacheLocation = CacheLocation.LocalStorage): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            switch (location) {
                case CacheLocation.LocalStorage:
                    if (LocalForage.getItem(key))
                        LocalForage.removeItem(key);
                    LocalForage.setItem(key, value, function (err) {
                        // if err is non-null, we got an error
                        if (err) {
                            reject(err);
                        } else {
                            resolve();
                        }
                    });
                    break;
                case CacheLocation.SessionStorage:
                    {
                        if (this.sessionStorageHolder.getItem(key))
                            this.sessionStorageHolder.removeItem(key);
                        const added = this.sessionStorageHolder.addItem(key, value);
                        if (added)
                            resolve(value);
                        break;
                    }
                default:
            }
        });
    }

    public remove(key: string, location: CacheLocation = CacheLocation.LocalStorage, rejectOnNotFound: boolean = false): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            switch (location) {
                case CacheLocation.LocalStorage:
                    LocalForage.removeItem(key, function (err) {
                        // if err is non-null, we got an error
                        if (err && rejectOnNotFound) {
                            reject(err);
                        } else {
                            resolve();
                        }
                    });
                    break;
                case CacheLocation.SessionStorage:
                    this.sessionStorageHolder.removeItem(key);
                    resolve();
                    break;
                default:
            }
        });
    }

    public removeKeyContains(xKey: string, location: CacheLocation = CacheLocation.LocalStorage): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            switch (location) {
                case CacheLocation.LocalStorage:
                    LocalForage.keys((x, keys) => {
                        const promises = [];
                        for (const key of Enumerable.from<string>(keys).where(x => x.toLowerCase().indexOf(xKey.toLowerCase()) !== -1).toArray()) {
                            promises.push(this.remove(key));
                        }

                        Promise.all(promises).then(() => {
                            resolve();
                        }, () => {
                            reject();
                        });
                    });
                    break;
                case CacheLocation.SessionStorage:
                    this.sessionStorageHolder.removeItemContains(xKey);
                    resolve();
                    break;
                default:
            }

        });
    }
}

export enum CacheLocation {
    SessionStorage,
    LocalStorage
}

class SessionStorage {
    _internalStorage: { [id: string]: any; } = {};

    addItem(key: string, item: any): boolean {
        this._internalStorage[key] = item;
        return true;
    }

    getItem(key: string) {
        return this._internalStorage[key];
    }

    removeItem(key: string) {
        if (key in this._internalStorage) {
            delete this._internalStorage[key];
        }
    }

    removeItemContains(key: string) {
        const keys = Object.keys(this._internalStorage);
        key = keys.filter(z => z.indexOf(key) > -1)[0];
        this.removeItem(key);
    }

    getKeys() {
        return Object.keys(this._internalStorage);
    }
}


export class CacheKeys {
    public static selectedFilters = "selectedFilters";
    public static timezone = "timezone";
    public static cultureCode = "cultureCode";
}
